import { FACILITY_NAMES } from '../utils/facility-names';
import { capitalizeWords, splitCamelCase } from '../utils/text-case';
import type { AdvancedSearchOptionLabels } from '../core/services/advanced-search-options.service';

export const RISK_NEED_LEVEL_LABELS: Record<string, string> = {
  '0': 'Unassessed',
  '1': 'Low',
  '2': 'Moderate',
  '3': 'High',
  '5': 'Very High',
  '6': 'Administrative',
  '9': 'Unassigned'
};

export const OFFENDER_CUSTODY_STATUS_LABELS: Record<string, string> = {
  'A': 'Active',
  'T': 'Terminated'
};

export const GENDER_LABELS: Record<string, string> = {
  'M': 'Male',
  'F': 'Female',
  'U': 'Unknown'
};

export const RACE_LABELS: Record<string, string> = {
  'B': 'Black',
  'O': 'Bi-Racial',
  'W': 'White',
  'A': 'Asian or Pacific Islander',
  'H': 'Hispanic/Latino',
  'I': 'American Indian/Alaskan Native',
  'U': 'Unknown'
};

export const HAIR_COLOR_LABELS: Record<string, string> = {
  'B': 'Brown',
  'D': 'Blond or Strawberry',
  'E': 'Green',
  'G': 'Gray or Partially Gray',
  'I': 'Pink',
  'K': 'Black',
  'L': 'Blue',
  'N': 'Bald',
  'O': 'Orange',
  'P': 'Purple',
  'R': 'Red or Auburn',
  'S': 'Sandy',
  'U': 'Unknown',
  'W': 'White'
};

export const EYE_COLOR_LABELS: Record<string, string> = {
  '0': 'Unknown',
  '1': 'Black',
  '2': 'Blue',
  '3': 'Brown',
  '4': 'Gray',
  '5': 'Green',
  '6': 'Hazel',
  '7': 'Maroon',
  '8': 'Pink',
  '9': 'Multicolored'
};

function codeLabel(labels: Record<string, string>, code: string | number | null | undefined): string {
  if (code == null) return '';
  const trimmed = String(code).trim();
  if (trimmed === '') return '';
  return labels[trimmed.toUpperCase()] ?? trimmed;
}

export function getRiskAssessmentLabel(code: string | number | null | undefined): string {
  if (code == null || code === '') return '';
  const strCode = String(code).trim();
  return RISK_NEED_LEVEL_LABELS[strCode] ?? strCode;
}

export function getGenderLabel(code: string | null | undefined): string {
  return codeLabel(GENDER_LABELS, code);
}

export function getRaceLabel(code: string | null | undefined): string {
  return codeLabel(RACE_LABELS, code);
}

export function getHairColorLabel(code: string | null | undefined): string {
  return codeLabel(HAIR_COLOR_LABELS, code);
}

export function getEyeColorLabel(code: string | number | null | undefined): string {
  return codeLabel(EYE_COLOR_LABELS, code);
}

export function getOffenderStatusLabel(statusCode: string | null | undefined): string {
  if (!statusCode) return '';
  const trimmed = statusCode.trim();
  const upper = trimmed.toUpperCase();
  return OFFENDER_CUSTODY_STATUS_LABELS[upper] ?? trimmed;
}

const IDENTIFIER_KEYS = new Set(['nameOrId', 'pid', 'docNumber', 'soNumber']);

const humanize = (value: string): string =>
  capitalizeWords(splitCamelCase(value).replace(/[-_]+/g, ' '));

const lookup = (labels: Record<string, string> | undefined, value: string): string | undefined => {
  if (!labels) {
    return undefined;
  }
  const trimmed = value.trim();
  return labels[value] ?? labels[trimmed] ?? labels[trimmed.toUpperCase()] ?? labels[trimmed.toLowerCase()];
};

const COUNTY_KEYS = new Set(['countyOfSupervision', 'convictionCounty']);

const countyName = (value: string): string | undefined =>
  (/^\d{1,3}$/.test(value) ? FACILITY_NAMES[value.padStart(3, '0')] : undefined);

export function getDynamicOptionLabel(
  key: string,
  value: string,
  labels?: AdvancedSearchOptionLabels
): string {
  if (!value) {
    return '';
  }

  if (IDENTIFIER_KEYS.has(key)) {
    return value;
  }

  const fromApi = lookup(labels?.[key], value);
  if (fromApi !== undefined) {
    return fromApi;
  }

  const trimmed = value.trim();

  if (COUNTY_KEYS.has(key)) {
    return countyName(trimmed) ?? humanize(trimmed);
  }

  if (key === 'facility') {
    return FACILITY_NAMES[trimmed] ?? FACILITY_NAMES[trimmed.toUpperCase()] ?? humanize(trimmed);
  }

  return humanize(trimmed);
}